/* abox.nz theme — front-end behavior
   - Cart drawer (open/close/add/remove/update)
   - Product variant selection + add-to-cart
   - Quantity stepper
*/
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const money = (cents, currency = 'NZD') =>
    new Intl.NumberFormat('en-NZ', { style: 'currency', currency }).format(cents / 100);

  /* -------- Cart store -------- */
  const Cart = {
    state: null,
    listeners: new Set(),
    subscribe(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); },
    notify() { this.listeners.forEach(fn => fn(this.state)); },
    async fetch() {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      this.state = await res.json();
      this.notify();
      return this.state;
    },
    async add(items) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart');
      }
      await this.fetch();
      return this.state;
    },
    async change({ id, quantity }) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id, quantity }),
      });
      this.state = await res.json();
      this.notify();
      return this.state;
    },
  };

  /* -------- Cart drawer -------- */
  function initCartDrawer() {
    const drawer = $('[data-cart-drawer]');
    const backdrop = $('[data-cart-backdrop]');
    if (!drawer) return;

    const open = () => {
      drawer.setAttribute('open', '');
      backdrop?.setAttribute('data-open', 'true');
      document.body.style.overflow = 'hidden';
    };
    const close = () => {
      drawer.removeAttribute('open');
      backdrop?.setAttribute('data-open', 'false');
      document.body.style.overflow = '';
    };

    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-cart-open]');
      const closer = e.target.closest('[data-cart-close]');
      if (opener) { e.preventDefault(); open(); }
      if (closer) { e.preventDefault(); close(); }
    });
    backdrop?.addEventListener('click', close);

    Cart.subscribe(renderDrawer);
    Cart.fetch();

    function renderDrawer(cart) {
      const body = $('[data-cart-body]', drawer);
      const foot = $('[data-cart-foot]', drawer);
      const count = $('[data-cart-count]');
      if (count) count.textContent = cart.item_count;

      if (!cart.items.length) {
        body.innerHTML = `<p class="t-body" style="text-align:center;padding:48px 0;opacity:.6;">Your cart is empty.</p>`;
        foot.innerHTML = `<a class="btn btn--invert btn--block" href="/" data-cart-close>Back to home</a>`;
        return;
      }

      body.innerHTML = cart.items.map(line => `
        <article class="cart-line" data-line-key="${line.key}">
          <div class="cart-line__media">
            ${line.image ? `<img src="${line.image}" alt="${escape(line.product_title)}" loading="lazy">` : ''}
          </div>
          <div>
            <p class="cart-line__title">${escape(line.product_title)}</p>
            ${line.variant_title && line.variant_title !== 'Default Title' ? `<p class="cart-line__variant">${escape(line.variant_title)}</p>` : ''}
            <p class="cart-line__variant">${money(line.final_line_price, cart.currency)}</p>
            <button class="cart-line__remove" data-line-remove="${line.key}">Remove</button>
          </div>
          <div class="product__qty" style="height:fit-content;">
            <button data-line-decrease="${line.key}" aria-label="Decrease">−</button>
            <input type="text" value="${line.quantity}" readonly aria-label="Quantity">
            <button data-line-increase="${line.key}" aria-label="Increase">+</button>
          </div>
        </article>
      `).join('');

      const threshold = Number(drawer.dataset.shippingThreshold || 0) * 100;
      const shippingBar = threshold > 0
        ? `
          <div>
            <p class="t-label" style="display:flex;justify-content:space-between;margin:0 0 var(--space-8);">
              <span>${cart.total_price >= threshold ? 'Free shipping unlocked' : 'Free shipping at ' + money(threshold, cart.currency)}</span>
              <span>${money(cart.total_price, cart.currency)}</span>
            </p>
            <div class="shipping-bar"><div class="shipping-bar__fill" style="width:${Math.min(100, (cart.total_price/threshold)*100)}%;"></div></div>
          </div>
        ` : '';

      foot.innerHTML = `
        ${shippingBar}
        <div class="cart-totals mt-24"><span>Subtotal</span><span>${money(cart.total_price, cart.currency)}</span></div>
        <p class="t-body" style="opacity:.6;margin:0 0 var(--space-12);">Secure checkout · Cancel anytime.</p>
        <a class="btn btn--invert btn--block" href="/checkout">Checkout</a>
      `;
    }

    drawer.addEventListener('click', async (e) => {
      const remove = e.target.closest('[data-line-remove]');
      const inc = e.target.closest('[data-line-increase]');
      const dec = e.target.closest('[data-line-decrease]');
      if (!remove && !inc && !dec) return;
      const key = (remove || inc || dec).dataset.lineRemove
        || (inc && inc.dataset.lineIncrease)
        || (dec && dec.dataset.lineDecrease);
      const line = Cart.state?.items.find(i => i.key === key);
      if (!line) return;
      let qty = line.quantity;
      if (remove) qty = 0;
      if (inc) qty += 1;
      if (dec) qty -= 1;
      await Cart.change({ id: key, quantity: Math.max(0, qty) });
    });

    // Expose an event-driven add hook for the product form.
    document.addEventListener('cart:add', async (e) => {
      try {
        await Cart.add(e.detail.items);
        open();
      } catch (err) {
        alert(err.message);
      }
    });
  }

  /* -------- Product form -------- */
  function initProductForms() {
    $$('[data-product-form]').forEach(form => {
      const data = JSON.parse($('[data-product-json]', form).textContent);
      const variants = data.variants;
      const optionGroups = $$('[data-option-index]', form);
      const addBtn = $('[data-add-to-cart]', form);
      const priceEl = $('[data-product-price]');
      const qtyInput = $('[data-qty-input]', form);

      const selected = data.selected_or_first_available_variant.options.slice();

      function pickVariant() {
        return variants.find(v => v.options.every((opt, i) => opt === selected[i]));
      }

      function refresh() {
        const variant = pickVariant();
        // Update price
        if (priceEl && variant) {
          priceEl.innerHTML = variant.compare_at_price && variant.compare_at_price > variant.price
            ? `<s>${money(variant.compare_at_price, data.currency || 'NZD')}</s>${money(variant.price, data.currency || 'NZD')}`
            : money(variant.price, data.currency || 'NZD');
        }
        // Update url
        if (variant) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url);
        }
        // Update button state
        if (addBtn) {
          if (!variant) { addBtn.disabled = true; addBtn.textContent = 'Unavailable'; }
          else if (!variant.available) { addBtn.disabled = true; addBtn.textContent = 'Sold out'; }
          else { addBtn.disabled = false; addBtn.textContent = addBtn.dataset.label || 'Add to cart'; }
        }
      }

      optionGroups.forEach(group => {
        const idx = Number(group.dataset.optionIndex);
        $$('.option-pill', group).forEach(btn => {
          btn.addEventListener('click', () => {
            selected[idx] = btn.dataset.value;
            $$('.option-pill', group).forEach(b => b.setAttribute('aria-checked', b === btn ? 'true' : 'false'));
            refresh();
          });
        });
      });

      // Quantity stepper
      $$('[data-qty-step]', form).forEach(btn => {
        btn.addEventListener('click', () => {
          const step = Number(btn.dataset.qtyStep);
          const next = Math.max(1, Number(qtyInput.value) + step);
          qtyInput.value = next;
        });
      });

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const variant = pickVariant();
        if (!variant || !variant.available) return;
        
        // Check if this is a "Buy Now" button (goes directly to checkout)
        const isBuyNow = addBtn?.dataset.buyNow === 'true';
        
        if (isBuyNow) {
          // Add to cart and redirect to checkout immediately
          try {
            addBtn.disabled = true;
            addBtn.textContent = 'Processing...';
            await Cart.add([{ id: variant.id, quantity: Number(qtyInput?.value || 1) }]);
            window.location.href = '/checkout';
          } catch (err) {
            alert(err.message);
            addBtn.disabled = false;
            addBtn.textContent = addBtn.dataset.label || 'Purchase now';
          }
        } else {
          // Normal flow: add to cart and open drawer
          document.dispatchEvent(new CustomEvent('cart:add', {
            detail: { items: [{ id: variant.id, quantity: Number(qtyInput?.value || 1) }] }
          }));
        }
      });

      // Gallery thumbs
      const main = $('[data-gallery-main] img');
      $$('[data-gallery-thumb]').forEach(thumb => {
        thumb.addEventListener('click', () => {
          $$('[data-gallery-thumb]').forEach(t => t.setAttribute('aria-current', 'false'));
          thumb.setAttribute('aria-current', 'true');
          if (main) main.src = thumb.dataset.src;
        });
      });

      refresh();
    });
  }

  /* -------- Helpers -------- */
  function escape(s) { return String(s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c])); }

  /* -------- Scroll reveal -------- */
  function initScrollReveal() {
    const revealEls = $$('[data-reveal]');
    if (!revealEls.length) return;

    if (!('IntersectionObserver' in window)) {
      revealEls.forEach(el => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -60px 0px', threshold: 0.1 });

    revealEls.forEach(el => observer.observe(el));
  }

  /* -------- FAQ smooth scroll lock -------- */
  function initFAQ() {
    $$('[data-faq] details').forEach(details => {
      details.addEventListener('toggle', () => {
        if (details.open) {
          const parent = details.closest('[data-faq]');
          if (parent) {
            $$('[data-faq] details[open]').forEach(d => { if (d !== details) d.open = false; });
          }
        }
      });
    });
  }

  /* -------- Boot -------- */
  document.addEventListener('DOMContentLoaded', () => {
    initCartDrawer();
    initProductForms();
    initScrollReveal();
    initFAQ();
  });
})();
